import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  useGetOrderQuery,
  useUpdateOrderStatusMutation,
  useUploadOrderReceiptMutation,
  useManualConfirmOrderMutation,
  useGetProfileQuery,
} from "../store/trader-api";

const Container = styled.div`
  background: var(--gradient-bg);
  color: var(--text-primary);
  min-height: 100vh;
  padding: 1rem;
  font-family: inherit;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
    gap: 0.5rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: var(--text-primary);
  }

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  max-width: 1400px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const LeftPanel = styled.div`
  background: var(--bg-glass);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-light);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-card);

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }
`;

const RightPanel = styled.div`
  background: var(--bg-glass);
  backdrop-filter: blur(12px);
  border: 1px solid var(--border-light);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--shadow-card);

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0.75rem;
  }
`;

const DealHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const DealTitle = styled.h1`
  font-size: 2rem;
  font-weight: normal;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const DealId = styled.span`
  background: var(--accent-primary-light);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-family: monospace;
  color: var(--accent-primary);
  border: 1px solid var(--accent-primary-border);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.25rem 0.5rem;
    max-width: calc(100vw - 4rem);
  }
`;

const StatusSection = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
    margin-bottom: 0.75rem;
  }
`;

const StatusItem = styled.div`
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.375rem 0;
    border-bottom: 1px solid var(--border-divider);

    &:last-child {
      border-bottom: none;
    }
  }
`;

const StatusLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    margin-bottom: 0;
    flex-shrink: 0;
  }
`;

const StatusValue = styled.div`
  font-size: 1.1rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
    text-align: right;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  background: ${(props) => {
    switch (props.status) {
      case "completed":
        return "var(--accent-neutral)";
      case "pending":
        return "var(--accent-primary)";
      case "processing":
        return "var(--accent-primary)";
      case "failed":
        return "var(--accent-error)";
      default:
        return "var(--text-secondary)";
    }
  }};
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const AmountSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const AmountItem = styled.div`
  text-align: center;

  @media (max-width: 768px) {
    text-align: left;
    background: var(--bg-primary);
    padding: 0.5rem 0.625rem;
    border-radius: 0.5rem;
  }
`;

const AmountLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 0.625rem;
    margin-bottom: 0.125rem;
  }
`;

const AmountDescription = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
  font-style: italic;

  @media (max-width: 768px) {
    display: none;
  }
`;

const AmountValue = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${(props) =>
    props.positive ? "var(--accent-neutral)" : props.negative ? "var(--accent-error)" : "var(--text-primary)"};

  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 0.9375rem;
    margin-bottom: 0.5rem;
  }
`;

const HistorySection = styled.div``;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileHistoryList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
`;

const MobileHistoryCard = styled.div`
  background: var(--bg-card);
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid var(--border-divider);
`;

const MobileHistoryOp = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin-bottom: 0.125rem;
  line-height: 1.4;
`;

const MobileHistoryMeta = styled.div`
  font-size: 0.625rem;
  color: var(--text-muted);
  display: flex;
  gap: 0.5rem;
`;

const MobileHistoryAmount = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props) => (props.positive ? 'var(--text-secondary)' : props.negative ? 'var(--text-muted)' : 'var(--text-primary)')};
  margin-top: 0.25rem;
`;

const HistoryRow = styled.tr`
  border-bottom: 1px solid var(--border-light);
`;

const HistoryCell = styled.td`
  padding: 1rem 0.5rem;
  font-size: 0.9rem;
  vertical-align: top;
  color: var(--text-primary);

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }
`;

const HistoryOperation = styled.div`
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const HistoryUser = styled.div`
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const HistoryTime = styled.div`
  color: var(--text-secondary);
  text-align: center;
`;

const HistoryAmount = styled.div<{ positive?: boolean; negative?: boolean }>`
  font-weight: 500;
  color: ${(props) =>
    props.positive ? "var(--text-secondary)" : props.negative ? "var(--text-muted)" : "var(--text-primary)"};
`;

const HistoryBalance = styled.div`
  text-align: right;
  font-family: monospace;
`;

const AttachmentsSection = styled.div`
  margin-bottom: 2rem;
`;

const AttachmentTabs = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-light);
  margin-bottom: 1rem;
`;

const AttachmentTab = styled.button<{ active?: boolean }>`
  background: none;
  border: none;
  color: ${(props) => (props.active ? "var(--accent-primary)" : "var(--text-secondary)")};
  padding: 1rem 2rem;
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? "var(--accent-primary)" : "transparent")};
  font-size: 1rem;

  &:hover {
    color: var(--text-primary);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
  }
`;

const AttachmentContent = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.8125rem;
  }
`;

const AttachmentIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }
`;

const UploadButton = styled.button`
  background: var(--accent-primary);
  color: #ffffff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--accent-primary-hover);
  }

  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"trader" | "buyer">("trader");
  const [isUploading, setIsUploading] = useState(false);

  const { data: order, isLoading, error } = useGetOrderQuery(orderId || "");
  const [updateOrderStatus, { isLoading: isUpdating }] =
    useUpdateOrderStatusMutation();

  const [uploadReceipt] = useUploadOrderReceiptMutation();
  const [manualConfirm, { isLoading: isManualConfirming }] = useManualConfirmOrderMutation();
  const { data: profile } = useGetProfileQuery();

  const handleUploadTraderReceipt = () => {
    // Создаем скрытый input для выбора файла
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,.pdf";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && orderId) {
        setIsUploading(true);
        try {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("type", "trader"); // Указываем тип чека

          if (!uploadReceipt) {
            throw new Error("API для загрузки чеков не реализован");
          }
          await uploadReceipt({ orderId, formData }).unwrap();
          alert(`Чек трейдера "${file.name}" успешно загружен!`);
        } catch (error) {
          console.error("Ошибка загрузки файла:", error);
          alert("Произошла ошибка при загрузке файла. Попробуйте еще раз.");
        } finally {
          setIsUploading(false);
        }
      }
    };
    input.click();
  };

  const handleCompleteOrder = async () => {
    if (
      window.confirm("Вы уверены, что хотите подтвердить получение средств?")
    ) {
      try {
        await updateOrderStatus({
          orderId: orderId!,
          status: "COMPLETED",
        }).unwrap();
        alert("Ордер успешно завершен");
      } catch (err) {
        console.error("Ошибка при завершении ордера:", err);
        alert("Не удалось завершить ордер");
      }
    }
  };

  const handleManualConfirm = async () => {
    if (
      window.confirm(
        "Вы подтверждаете, что получили средства и хотите вручную завершить ордер?"
      )
    ) {
      try {
        await manualConfirm(orderId!).unwrap();
        alert("Ордер успешно подтвержден вручную");
      } catch (err) {
        console.error("Ошибка при ручном подтверждении:", err);
        alert("Не удалось подтвердить ордер");
      }
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "PENDING":
        return "Ожидает";
      case "PROCESSING":
        return "Обработка";
      case "COMPLETED":
        return "Завершен";
      case "CANCELLED":
        return "Отменен";
      case "FAILED":
        return "Ошибка";
      case "EXPIRED":
        return "Истек";
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div style={{ padding: "2rem", textAlign: "center" }}>Загрузка...</div>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          Ордер не найден
        </div>
      </Container>
    );
  }

  // Сначала создаем базовые данные
  const baseOrderData = {
    id: order.id || orderId || "N/A",
    status: order.status || "unknown",
    operation: "Пополнение",
    createdAt: order.createdAt
      ? new Date(order.createdAt).toLocaleString("ru-RU")
      : "—",
    lastUpdate: order.updatedAt
      ? new Date(order.updatedAt).toLocaleString("ru-RU")
      : "—",
    debitAmount: order.usdtAmount || 0,
    requestedAmount: order.localAmount || order.amount || 0,
    creditAmount: order.localAmount || order.amount || 0,
    currency: "RUB",
    exchangeRate: order.exchangeRate || 0,
  };

  // Парсим метаданные для получения информации о чеках и суммах
  let receipts: any = {};
  let metadata: any = {};
  try {
    metadata =
      typeof order.metadata === "string"
        ? JSON.parse(order.metadata)
        : order.metadata || {};
    receipts = metadata?.receipts || {};
  } catch (e) {
    console.warn("Не удалось распарсить metadata:", e);
  }

  // Затем создаем историю на основе базовых данных
  const history = [];
  const frozenAmount = baseOrderData.debitAmount; // Сумма в USDT

  // Получаем комиссию трейдера из метаданных или из поля ордера
  const traderCommission = metadata.entityCommission || order.traderFee || 0;

  // Calculate Fee Percentage - используем сохранённый процент тарифа из метаданных
  const rawFeePercent =
    metadata?.entityFeePercentage
    ?? metadata?.frozenTraderTariff?.feePercentage
    ?? (order.traderFee && order.usdtAmount && order.usdtAmount > 0
        ? (order.traderFee / order.usdtAmount * 100)
        : null);
  const feePercent = rawFeePercent != null
    ? Number(rawFeePercent).toFixed(2)
    : "0.00";

  // Check for appeals
  const hasAppeals = order.appeals && order.appeals.length > 0;

  // 1. Заморозка баланса при создании ордера
  history.push({
    operation: "Пополнение - Заморозка баланса при старте ордера",
    user: "Система",
    time: baseOrderData.createdAt,
    amount: -frozenAmount.toFixed(2),
    balance: `Заморожено ${frozenAmount.toFixed(2)} USDT`,
    negative: true,
  });

  // 2. Если ордер завершен успешно - показываем правильные операции с балансом
  if (order.status === "COMPLETED") {
    // Шаг 1: Разморозка замороженной суммы
    history.push({
      operation: "Пополнение - Разморозка баланса при завершении",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: -frozenAmount.toFixed(2),
      balance: `Разморожено ${frozenAmount.toFixed(2)} USDT`,
      negative: false,
    });

    // Шаг 2: Списание полной суммы ордера
    history.push({
      operation: "Пополнение - Списание суммы ордера",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: -frozenAmount.toFixed(2),
      balance: `Списано ${frozenAmount.toFixed(2)} USDT`,
      negative: true,
    });

    // Шаг 3: Зачисление дохода трейдера на рабочий баланс
    history.push({
      operation: "Пополнение - Зачисление на рабочий баланс",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: traderCommission > 0 ? `+${traderCommission.toFixed(2)}` : "0.00",
      balance: `Зачислено на рабочий баланс ${traderCommission.toFixed(2)} USDT`,
      positive: traderCommission > 0,
    });
  }

  // 3. Если ордер отменен - разморозка
  if (["CANCELLED", "FAILED", "EXPIRED"].includes(order.status)) {
    history.push({
      operation: "Пополнение - Разморозка баланса при отмене",
      user: "Система",
      time: baseOrderData.lastUpdate,
      amount: frozenAmount.toFixed(2),
      balance: `Разморожено ${frozenAmount.toFixed(2)} USDT`,
      positive: true,
    });
  }

  // Финальный объект с историей
  const orderData = {
    ...baseOrderData,
    history,
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>← Назад</BackButton>
      </Header>

      <MainContent>
        <LeftPanel>
          <DealHeader>
            <DealTitle>Сделка</DealTitle>
            <DealId>ID {orderData.id}</DealId>

            {/* Кнопка ручного подтверждения (только если разрешено админом и не прошло 48ч) */}
            {["PENDING", "EXPIRED", "CANCELLED"].includes(orderData.status) && 
             profile?.canConfirmManually && 
             (Date.now() - new Date(order.createdAt).getTime()) / (1000 * 60 * 60) <= 48 && (
              <UploadButton
                onClick={handleManualConfirm}
                disabled={isManualConfirming || isUpdating}
                style={{
                  background: "transparent",
                  color: "var(--accent-primary)",
                  border: "1px solid var(--accent-primary)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  marginLeft: "auto",
                  whiteSpace: "nowrap",
                  fontSize: "0.8125rem",
                  padding: "0.5rem 1rem",
                  marginTop: 0,
                }}
                title="Ручное подтверждение"
              >
                {isManualConfirming ? "Подтверждение..." : "Подтвердить вручную"}
              </UploadButton>
            )}
          </DealHeader>

          <StatusSection>
            <StatusItem>
              <StatusLabel>Статус</StatusLabel>
              <StatusValue>
                <StatusBadge status={orderData.status}>
                  {getStatusText(orderData.status)}
                </StatusBadge>
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Операция</StatusLabel>
              <StatusValue style={{ color: "var(--accent-primary)" }}>
                {orderData.operation}
              </StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Дата создания</StatusLabel>
              <StatusValue>{orderData.createdAt}</StatusValue>
            </StatusItem>
          </StatusSection>

          <StatusSection>
            <StatusItem>
              <StatusLabel>Последнее изм.</StatusLabel>
              <StatusValue>{orderData.lastUpdate}</StatusValue>
            </StatusItem>
            <StatusItem>
              <StatusLabel>Метод оплаты</StatusLabel>
              <StatusValue>
                {metadata.paymentMethod ||
                  order.requisite?.method ||
                  (order.isSbp
                    ? "SBP"
                    : order.cardNumber
                    ? "CARD_NUMBER"
                    : "—")}
              </StatusValue>
            </StatusItem>
            {order.requisite?.tag && (
              <StatusItem>
                <StatusLabel>Название реквизита</StatusLabel>
                <StatusValue>{order.requisite.tag}</StatusValue>
              </StatusItem>
            )}
            <StatusItem>
              <StatusLabel>Реквизит</StatusLabel>
              <StatusValue>
                {order.bank ? `${order.bank} ` : ""}
                {order.requisite?.cardNumber ||
                  order.requisite?.sbpPhoneNumber ||
                  order.requisite?.phoneNumber ||
                  order.requisite?.accountNumber ||
                  order.cardNumber ||
                  order.phoneNumber ||
                  "—"}
              </StatusValue>
            </StatusItem>
          </StatusSection>

          <StatusSection>
            <StatusItem>
              <StatusLabel>Комиссия</StatusLabel>
              <StatusValue>
                {traderCommission.toFixed(2)} USDT ({feePercent}%)
              </StatusValue>
            </StatusItem>
          </StatusSection>

          <AmountSection>
            <AmountItem>
              <AmountLabel>Сумма списания</AmountLabel>
              <AmountDescription>
                Списано с баланса трейдера
              </AmountDescription>
              <AmountValue negative>
                - {orderData.debitAmount.toFixed(2)} $
              </AmountValue>
            </AmountItem>
            <AmountItem>
              <AmountLabel>Заявленная сумма</AmountLabel>
              <AmountDescription>Сумма, указанная клиентом</AmountDescription>
              <AmountValue positive>
                + {orderData.requestedAmount} {orderData.currency}
              </AmountValue>
            </AmountItem>
            <AmountItem>
              <AmountLabel>Сумма зачисления</AmountLabel>
              <AmountDescription>
                Фактически зачислено трейдеру
              </AmountDescription>
              <AmountValue positive>
                + {orderData.creditAmount} {orderData.currency}
              </AmountValue>
            </AmountItem>
            <AmountItem>
              <AmountLabel>Курс обмена</AmountLabel>
              <AmountValue>
                {orderData.exchangeRate
                  ? `${orderData.exchangeRate.toFixed(2)} ₽/USDT`
                  : "—"}
              </AmountValue>
            </AmountItem>
          </AmountSection>

          <HistorySection>
            <SectionTitle>История изменений сделки</SectionTitle>
            <HistoryTable>
              <thead>
                <HistoryRow>
                  <HistoryCell>
                    <strong>Операция</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Пользователь</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Время</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Сумма</strong>
                  </HistoryCell>
                  <HistoryCell>
                    <strong>Баланс до → после</strong>
                  </HistoryCell>
                </HistoryRow>
              </thead>
              <tbody>
                {orderData.history.map((item, index) => (
                  <HistoryRow key={index}>
                    <HistoryCell>
                      <HistoryOperation>{item.operation}</HistoryOperation>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryUser>{item.user}</HistoryUser>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryTime>{item.time}</HistoryTime>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryAmount
                        positive={!!item.positive}
                        negative={!!item.negative}
                      >
                        {item.amount !== 0 && (item.positive ? "+" : "")}
                        {item.amount !== 0 ? `${item.amount} $` : "0.0000 $"}
                      </HistoryAmount>
                    </HistoryCell>
                    <HistoryCell>
                      <HistoryBalance>{item.balance}</HistoryBalance>
                      {item.negative && (
                        <div style={{ color: "var(--accent-error)", fontSize: "0.8rem" }}>
                          {item.negative}
                        </div>
                      )}
                    </HistoryCell>
                  </HistoryRow>
                ))}
              </tbody>
            </HistoryTable>

            {/* Mobile history cards */}
            <MobileHistoryList>
              {orderData.history.map((item, index) => (
                <MobileHistoryCard key={index}>
                  <MobileHistoryOp>{item.operation}</MobileHistoryOp>
                  <MobileHistoryMeta>
                    <span>{item.user}</span>
                    <span>•</span>
                    <span>{item.time}</span>
                  </MobileHistoryMeta>
                  <MobileHistoryAmount
                    positive={!!item.positive}
                    negative={!!item.negative}
                  >
                    {item.amount !== 0 && (item.positive ? "+" : "")}
                    {item.amount !== 0 ? `${item.amount} $` : "0.0000 $"}
                  </MobileHistoryAmount>
                </MobileHistoryCard>
              ))}
            </MobileHistoryList>
          </HistorySection>
        </LeftPanel>

        <RightPanel>
          <AttachmentsSection>
            <SectionTitle>Прикрепленные чеки</SectionTitle>
            <AttachmentTabs>
              <AttachmentTab
                active={activeTab === "trader"}
                onClick={() => setActiveTab("trader")}
              >
                Трейдер
              </AttachmentTab>
            </AttachmentTabs>
            <AttachmentContent>
              {activeTab === "trader" ? (
                <>
                  {receipts.trader ? (
                    <>
                      <AttachmentIcon>✅</AttachmentIcon>
                      <div>Чек трейдера загружен</div>
                      <div
                        style={{
                          margin: "1rem 0",
                          fontSize: "0.9rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        <div>
                          <strong>Файл:</strong> {receipts.trader.originalName}
                        </div>
                        <div>
                          <strong>Размер:</strong>{" "}
                          {Math.round(receipts.trader.size / 1024)} KB
                        </div>
                        <div>
                          <strong>Загружен:</strong>{" "}
                          {new Date(receipts.trader.uploadedAt).toLocaleString(
                            "ru-RU"
                          )}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "1rem" }}>
                        <UploadButton
                          as="a"
                          href={`${process.env.REACT_APP_API_URL?.replace("/api", "") || "http://localhost:3001"}/uploads/receipts/${receipts.trader.filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Просмотреть
                        </UploadButton>
                        {hasAppeals && (
                          <UploadButton
                            onClick={handleUploadTraderReceipt}
                            disabled={isUploading}
                            style={{ background: "var(--text-secondary)" }}
                          >
                            {isUploading ? "Загрузка..." : "Заменить"}
                          </UploadButton>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <AttachmentIcon>📄</AttachmentIcon>
                      <div>Чеки трейдера еще не прикреплены</div>
                      {hasAppeals ? (
                        <UploadButton
                          onClick={handleUploadTraderReceipt}
                          disabled={isUploading}
                        >
                          {isUploading
                            ? "Загрузка..."
                            : "Прикрепить чек трейдера"}
                        </UploadButton>
                      ) : (
                        <div
                          style={{
                            marginTop: "1rem",
                            fontSize: "0.8rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          Загрузка чеков доступна только при наличии апелляции
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <AttachmentIcon>📄</AttachmentIcon>
                  <div>Чеки покупателя недоступны для провайдера</div>
                </>
              )}
            </AttachmentContent>
          </AttachmentsSection>
        </RightPanel>
      </MainContent>
    </Container>
  );
}

export default OrderDetail;
